import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import i18n from 'i18n-js';
import moment from 'moment';
import { FETCH_STATE } from 'constants/common';
import { buildUrl, formatAmount } from 'utils';
import { useStyles } from './Common.styled';
import { useHistory } from 'react-router-dom';
import { Box, Typography, Grid, Paper } from '@material-ui/core';
import { Card, Table } from 'components/UI';
import { TransactionsAction } from 'state/modules/transaction';

const PaymentsTable = ({ accountStatementId, refresh }) => {
  const classes = useStyles();
  const history = useHistory();
  const dispatch = useDispatch();
  const [params, setParams] = useState({
    page: 1,
    limit: 5,
    actionable: true,
    account_statement_id: accountStatementId,
  });

  const {
    list: data,
    pagination,
    fetchingTransaction: loading,
  } = useSelector((state) => state.transaction);

  const isLoading = loading === FETCH_STATE.LOADING;

  const columnsTransactions = [
    {
      name: i18n.t('common.id'),
      selector: 'id',
      grow: 0,
      minWidth: '6rem',
      sortable: false,
    },
    {
      name: i18n.t('common.partner'),
      selector: 'account_customer.customer_name',
      grow: 1,
      sortable: false,
    },
    {
      name: i18n.t('common.month'),
      selector: 'modified_time',
      format: (row) => `${moment(row.modified_time).format('MMMM')}`,
      grow: 1,
      sortable: false,
    },
    {
      name: i18n.t('common.year'),
      selector: 'modified_time',
      format: (row) => `${moment(row.modified_time).year()}`,
      grow: 1,
      sortable: false,
    },
    {
      name: i18n.t('common.total'),
      selector: 'amount',
      format: (row) => `$${formatAmount(row.amount || 0)}`,
      grow: 1,
      sortable: false,
    },
    {
      name: i18n.t('common.status'),
      selector: 'status',
      format: (row) => row?.status?.charAt(0).toUpperCase() + row?.status?.slice(1).toLowerCase(),
      grow: 1,
      sortable: false,
    },
  ];

  /**
   * Get comments
   * @param {object} params
   */
  const fetchTransactions = (params) => {
    dispatch(TransactionsAction.fetchTransactions(params));
  };

  /**
   * Handle page change
   * @param {object} event
   * @param {number} newPage New page selected
   */
  const handlePageChange = (newPage) => {
    const newParams = { ...params, page: newPage };
    setParams(newParams);
    fetchTransactions(newParams);
  };

  const handleRowClicked = (row) => {
    const { id } = row;
    history.push(buildUrl(`/payments/${id}`));
  };

  /**
   * Vertical menu list
   * It has the label and the function for each action
   */
  const items = [];

  const title = (
    <Box display="flex" alignItems="center" justifyContent="space-between">
      <Typography color="textPrimary" component="span">
        <Box fontSize={14} fontWeight="fontWeightMedium" data-cy="invoice-title">
          {i18n.t('account_statement.associated_payments', {
            payments: pagination?.totalRows || '-',
          })}
        </Box>
      </Typography>
    </Box>
  );

  useEffect(() => {
    fetchTransactions(params);
  }, [refresh]); // eslint-disable-line

  return (
    <Grid item xs={12}>
      <Card title={title} menuItems={items} headerActionType="vertical">
        <Box className={classes.container}>
          <Paper elevation={2} className={classes.paperInner}>
            <Table
              data={data}
              columns={columnsTransactions}
              loading={isLoading}
              page={params?.page}
              limit={params?.limit}
              handlePageChange={handlePageChange}
              handleRowClicked={handleRowClicked}
              totalRows={pagination?.totalRows || 0}
              handlePerRowsChange={null}
              showFirstButton={false}
              showLastButton={false}
              rowsPerPageOptions={[5]}
            />
          </Paper>
        </Box>
      </Card>
    </Grid>
  );
};

PaymentsTable.propTypes = {
  accountStatementId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  refresh: PropTypes.number,
};

export { PaymentsTable };
